import { useCallback, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { Popover } from "react-aria-components";
import styled from "@emotion/styled";
import { MDSCollectionChip } from "@/mds";
import { useAppStore } from "@/store";
import { TopicInfo } from "@/components/item-list/types";
import { CollectionPreview } from "@/components/item-preview/collection-preview/collection-preview";

interface HoverableCollectionChipProps {
  collection: TopicInfo;
  noteId?: string;
  templateId?: string;
  activePopoverCloseRef: React.MutableRefObject<(() => void) | null>;
}

export const HoverableCollectionChip = observer(
  ({ collection, noteId, templateId, activePopoverCloseRef }: HoverableCollectionChipProps) => {
    const { store } = useAppStore();
    const triggerRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const hideTimeout = useRef<ReturnType<typeof setTimeout>>();

    const close = useCallback(() => {
      clearTimeout(hideTimeout.current);
      setIsOpen(false);
    }, []);

    const showPopover = useCallback(() => {
      activePopoverCloseRef.current?.();
      clearTimeout(hideTimeout.current);
      activePopoverCloseRef.current = close;
      setIsOpen(true);
    }, [activePopoverCloseRef, close]);

    const hidePopover = useCallback(() => {
      hideTimeout.current = setTimeout(() => setIsOpen(false), 100);
    }, []);

    const cancelHide = useCallback(() => {
      clearTimeout(hideTimeout.current);
    }, []);

    const hasPreview = !!(noteId || templateId);

    return (
      <>
        <div
          ref={triggerRef}
          onMouseEnter={hasPreview ? showPopover : undefined}
          onMouseLeave={hasPreview ? hidePopover : undefined}
        >
          <MDSCollectionChip
            collectionId={collection.id}
            label={collection.label}
            onClick={() => store.navigation.goToCollection({ collectionId: collection.id })}
          />
        </div>
        {hasPreview && (
          <Popover
            triggerRef={triggerRef}
            isOpen={isOpen}
            onOpenChange={open => {
              if (!open) setIsOpen(false);
            }}
            isNonModal
            placement="bottom start"
          >
            <StyledPreviewContainer onMouseEnter={cancelHide} onMouseLeave={hidePopover}>
              <CollectionPreview
                collectionTopic={collection}
                noteId={noteId}
                templateId={templateId}
                onDismiss={() => setIsOpen(false)}
              />
            </StyledPreviewContainer>
          </Popover>
        )}
      </>
    );
  }
);

const StyledPreviewContainer = styled.div`
  background: ${({ theme }) => theme.colors.surface.popover};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  box-shadow: ${({ theme }) => theme.shadows.surface};
`;
